import React from 'react';
import type { Attribution } from '@/types/contact';
import type { QueryFieldOption } from '@/app/components/query-builder/types';

export const ATTRIBUTION_SOURCE_ICONS: Record<string, string> = {
  google_ads: '/images/attribution/google_ads.svg',
  linkedin_ads: '/images/attribution/linkedin_ads.svg',
  microsoft_ads: '/images/attribution/microsoft_ads.svg',
  meta_ads: '/images/attribution/meta_ads.svg',
  tiktok_ads: '/images/attribution/tiktok_ads.svg',
  x_ads: '/images/attribution/x_ads.svg',
  reddit_ads: '/images/attribution/reddit_ads.svg',
  quora_ads: '/images/attribution/quora_ads.svg',
  google: '/images/attribution/google.svg',
  bing: '/images/attribution/bing.svg',
  yahoo: '/images/attribution/yahoo.svg',
  duckduckgo: '/images/attribution/duckduckgo.svg',
  linkedin: '/images/attribution/linkedin.svg',
  linkedin_follow: '/images/attribution/linkedin_follow.svg',
  linkedin_comment: '/images/attribution/linkedin_comment.svg',
  linkedin_reaction: '/images/attribution/linkedin_reaction.svg',
  x: '/images/attribution/x.svg',
  facebook: '/images/attribution/facebook.svg',
  instagram: '/images/attribution/instagram.svg',
  reddit: '/images/attribution/reddit.svg',
  youtube: '/images/attribution/youtube.svg',
  outbound: '/images/attribution/outbound.svg',
  newsletter: '/images/attribution/newsletter.svg',
  nurture: '/images/attribution/nurture.svg',
  transactional: '/images/attribution/transactional.svg',
  customer_marketing: '/images/attribution/customer_marketing.svg',
  chatgpt: '/images/attribution/chatgpt.svg',
  perplexity: '/images/attribution/perplexity.svg',
  claude: '/images/attribution/claude.svg',
  gemini: '/images/attribution/gemini.svg',
  copilot: '/images/attribution/copilot.svg',
  direct: '/images/attribution/direct.svg',
  topic_intent: '/images/attribution/topic_intent.svg',
  product_intent: '/images/attribution/product_intent.svg',
  competitor_intent: '/images/attribution/competitor_intent.svg',
  other: '/images/attribution/other.svg',
};

export const ATTRIBUTION_SOURCE_NAMES: Record<string, string> = {
  google_ads: 'Google Ads',
  linkedin_ads: 'LinkedIn Ads',
  microsoft_ads: 'Microsoft Ads',
  meta_ads: 'Meta Ads',
  tiktok_ads: 'TikTok Ads',
  x_ads: 'X Ads',
  reddit_ads: 'Reddit Ads',
  quora_ads: 'Quora Ads',
  google: 'Google',
  bing: 'Bing',
  yahoo: 'Yahoo',
  duckduckgo: 'DuckDuckGo',
  linkedin: 'LinkedIn',
  linkedin_follow: 'LinkedIn Follow',
  linkedin_comment: 'LinkedIn Comment',
  linkedin_reaction: 'LinkedIn Reaction',
  x: 'X',
  facebook: 'Facebook',
  instagram: 'Instagram',
  reddit: 'Reddit',
  youtube: 'YouTube',
  outbound: 'Outbound',
  newsletter: 'Newsletter',
  nurture: 'Nurture',
  transactional: 'Transactional',
  customer_marketing: 'Customer Marketing',
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  claude: 'Claude',
  gemini: 'Gemini',
  copilot: 'Copilot',
  direct: 'Direct',
  topic_intent: 'Topic Intent',
  product_intent: 'Product Intent',
  competitor_intent: 'Competitor Intent',
  other: 'Other',
};

export interface ResolvedAttribution {
  sourceName: string;
  sourceLogo: string;
  type?: string;
  source?: string;
}

export const getAttributionPresentation = (
  attribution?: Attribution | null
): ResolvedAttribution | null => {
  if (!attribution) return null;

  const rawSource = attribution.source?.toLowerCase().trim() || '';
  const rawSourceName = attribution.source_name?.trim() || '';
  const rawLogo = attribution.source_logo?.trim() || '';

  const sourceName =
    rawSourceName || (rawSource ? ATTRIBUTION_SOURCE_NAMES[rawSource] || rawSource : '');
  const sourceLogo =
    rawLogo ||
    (rawSource && ATTRIBUTION_SOURCE_ICONS[rawSource] ? ATTRIBUTION_SOURCE_ICONS[rawSource] : '');

  if (!sourceName && !sourceLogo) return null;

  return {
    sourceName,
    sourceLogo,
    type: attribution.type,
    source: rawSource,
  };
};

export const getSignalAttribution = (signal: unknown): Attribution | undefined => {
  if (!signal || typeof signal !== 'object') return undefined;

  const record = signal as Record<string, unknown>;

  if (record.attribution && typeof record.attribution === 'object') {
    return record.attribution as Attribution;
  }

  const nestedKeys = ['website', 'market', 'linkedin', 'linkedin_signal'];
  for (const key of nestedKeys) {
    const nested = record[key];
    if (nested && typeof nested === 'object') {
      const nestedAttribution = (nested as Record<string, unknown>).attribution;
      if (nestedAttribution && typeof nestedAttribution === 'object') {
        return nestedAttribution as Attribution;
      }
    }
  }

  return undefined;
};

export const attributionSourceOptionItem = (option: QueryFieldOption) => {
  const iconUrl = ATTRIBUTION_SOURCE_ICONS[option.value];
  return (
    <div className="flex min-w-0 items-center gap-2">
      {iconUrl && (
        <img
          src={iconUrl}
          alt=""
          className="size-4 shrink-0 object-contain"
          onError={e => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <span className="min-w-0 truncate">{option.label}</span>
    </div>
  );
};

export const attributionSourceTagItem = (option: QueryFieldOption) => {
  const iconUrl = ATTRIBUTION_SOURCE_ICONS[option.value];
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      {iconUrl && (
        <img
          src={iconUrl}
          alt=""
          className="size-3.5 shrink-0 object-contain"
          onError={e => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <span className="min-w-0 truncate">{option.label}</span>
    </div>
  );
};
